import { formatWallClockDateTime } from '@/shared/lib/dateTime';
import type { KeyValueDataObject } from '@/shared/ui/KeyValueRow/KeyValueRow';
import {
    formatDateTime,
    formatOptionalBoolean,
    formatOptionalText,
} from '@/core/format';
import { Link } from '@/shared/ui/Link/Link';
import type { OrderResponse } from '@/core/generated/api';
import { CopyableValue } from '@/shared/ui/CopyableValue/CopyableValue';
import classes from './order-details.module.scss';

/** Подготавливает основные сведения о заказе */
export const getMainOrderDetailsData = (order: OrderResponse): KeyValueDataObject[] => [
    {
        label: 'Номер заказа',
        value: <CopyableValue
            value={order.orderNumber}
            label="Копировать номер"
        />,
    },
    {
        label: 'Тип заказа',
        value: order.orderType?.name || '',
    },
    {
        label: 'Тип питомца',
        value: order.petType?.name || '',
    },
    {
        label: 'Дата создания заказа (мск)',
        value: formatDateTime(order.created, true),
    },
    {
        label: 'Дата начала заказа (мск)',
        value: formatDateTime(order.startOrderTime || '', true),
    },
    {
        label: 'Дата окончания заказа (МСК)',
        value: formatDateTime(order.endOrderTime || '', true),
    },
    {
        label: 'Возврат питомца (местное время)',
        value: formatWallClockDateTime(order.orderSchedulePeriods?.slice(-1)[0]?.localEndTime),
    },
    {
        label: 'Дата начала заказа (локальное, местное время)',
        value: formatDateTime(order.startOrderLocalTime || ''),
    },
    {
        label: 'Статус',
        value: order.status?.name || '',
    },
    ...(order?.cancellationInitiator && order?.cancellationInitiator != null
        ? [{
            label: 'Инициатор отмены',
            value: order?.cancellationInitiator.name || '',
        }]
        : []),
    {
        label: 'Вознаграждение исполнителю',
        value: order.executorReward != null
            ? `${order.executorReward.toFixed(1)} руб.`
            : 'Не указано',
    },
    {
        label: 'Клиент',
        value: (<Link
            url={`/clients/${order.clientId}`}
            isBlank={true}
            text={(order.clientName || order.clientId) ?? 'Клиент не найден'}
        />),
    },
    {
        label: 'Исполнитель',
        value: (<Link
            url={`/executors/${order.executorId}`}
            isBlank={true}
            text={(order.executorFio || order.executorId) ?? 'Исполнитель не найден'}
        />),
    },
    {
        label: 'Питомцы в заказе',
        value: order?.orderPets && order?.orderPets?.length > 0
            ? (
                <ul className={classes.petList}>
                    {order.orderPets.map((pet, index) => (
                        <li
                            key={index}
                            className={classes.petListItem}
                        >
                            <span>
                                {pet.petBreed}{' '}
                                (<Link
                                    url={`/pets/${pet.petId}`}
                                    isBlank={true}
                                    text={pet.petName || 'Питомец'}
                                />)
                            </span>
                        </li>
                    ))}
                </ul>
            )
            : (
                'Нет питомцев в заказе'
            ),
    },

];

/** Подготавливает дополнительные пожелания к заказу */
export const getAdditionalOrderDetailsData = (order: OrderResponse): KeyValueDataObject[] => [
    {
        label: 'Нужно ли отработать команды',
        value: formatOptionalBoolean(order?.orderDetails?.isActiveCommand),
    },
    {
        label: 'Описание команд, которые нужно отработать',
        value: formatOptionalText(order?.orderDetails?.commandsDescription),
    },

    ...(order?.orderDetails?.specialistType != null
        ? [{
            label: 'Тип специалиста',
            value: order?.orderDetails?.specialistType.name || '',
        }]
        : []),

    ...(order?.orderDetails?.petPlacement != null
        ? [{
            label: 'Место оказания услуги',
            value: order?.orderDetails?.petPlacement.name || '',
        }]
        : []),

    ...(order?.orderDetails?.whoWillBringPet != null
        ? [{
            label: 'Кто перевезёт питомца',
            value: order?.orderDetails?.whoWillBringPet.name || '',
        }]
        : []),

    {
        label: 'Дополнительное описание (если заказана услуга "Меня не будет дома")',
        value: formatOptionalText(order?.orderDetails?.description),
    },
    {
        label: 'Комментарии к заказу',
        value: formatOptionalText(order?.orderDetails?.comments),
    },
    {
        label: 'Описание услуги для исполнителя',
        value: formatOptionalText(order?.orderDetails?.serviceDescriptionForExecutor),
    },
];
